import { useConvexAuth } from "convex/react";
import { Link } from "react-router";
import { useRef } from "react";
import { Dashboard } from "@/components/dashboard/dashboard";
import { Button } from "@/components/ui/button";
import { OnboardingFlow } from "@/components/onboarding/onboarding-flow";
import { useOnboarding } from "@/hooks/use-onboarding";

export default function Home() {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { needsOnboarding, isChecking, pendingPermissions, completeOnboarding } = useOnboarding();
  const wasAuthenticated = useRef(false);

  // Track if we've ever been authenticated to avoid flicker on back navigation
  if (isAuthenticated) {
    wasAuthenticated.current = true;
  }

  // Only show loading on initial app load, not on navigation
  // If we were previously authenticated, skip the loading state
  if (isLoading && !wasAuthenticated.current) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  // If loading but was previously authenticated, show dashboard (avoids flicker)
  if (isLoading && wasAuthenticated.current) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Dashboard />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 px-4 overflow-hidden">
        <div className="text-center max-w-md">
          <h1 className="text-6xl font-bold text-slate-900 dark:text-slate-100 mb-4">
            FlexTrack
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">
            Track your Amazon Flex earnings, hours, and tips
          </p>
          <div className="space-y-3 text-slate-600 dark:text-slate-400 mb-10">
            <p>✓ Log work blocks effortlessly</p>
            <p>✓ View weekly & monthly summaries</p>
            <p>✓ Track progress toward your goals</p>
          </div>
          <Button asChild size="lg" className="w-full">
            <Link to="/signin">Get Started</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Show onboarding if authenticated and needs onboarding
  if (needsOnboarding && !isChecking) {
    return (
      <OnboardingFlow
        pendingPermissions={pendingPermissions}
        onComplete={completeOnboarding}
      />
    );
  }

  // Show loading while checking permissions
  if (isChecking) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Dashboard />
    </div>
  );
}
